import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api, type AdminAccountListItem } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { AdminFormModal } from '@/components/AdminFormModal';
import { Loader2, Pencil, Shield, Trash2, UserPlus } from 'lucide-react';

function formatDate(s: string) {
  try {
    return new Date(s).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return s;
  }
}

export function Admins() {
  const { admin: currentAdmin } = useAuth();
  const [admins, setAdmins] = useState<AdminAccountListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [editingAdmin, setEditingAdmin] = useState<AdminAccountListItem | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const loadAdmins = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const { admins: list } = await api.getAdmins();
      setAdmins(list);
    } catch (err) {
      console.error('Failed to load admins:', err);
      setAdmins([]);
      setLoadError(err instanceof Error ? err.message : 'Ошибка загрузки');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAdmins();
  }, []);

  const handleDelete = async (item: AdminAccountListItem) => {
    if (!window.confirm(`Удалить администратора ${item.email}?`)) return;
    setDeletingId(item.id);
    try {
      await api.deleteAdmin(item.id);
      await loadAdmins();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : null;
      alert(msg || 'Не удалось удалить администратора');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="container mx-auto px-6 py-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Администраторы
          </CardTitle>
          <Button size="sm" onClick={() => setShowCreateModal(true)}>
            <UserPlus className="w-4 h-4 mr-2" />
            Добавить администратора
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Загрузка...
            </div>
          ) : loadError ? (
            <div className="text-sm text-destructive">{loadError}</div>
          ) : admins.length === 0 ? (
            <div className="text-sm text-muted-foreground">Администраторы не найдены</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-2 pr-4 font-medium">Email</th>
                  <th className="py-2 pr-4 font-medium">Создан</th>
                  <th className="py-2 pr-4 font-medium">Смена пароля</th>
                  <th className="py-2 pr-4 font-medium text-right">Действия</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((item) => {
                  const isSelf = item.id === currentAdmin?.id;
                  return (
                    <tr key={item.id} className="border-b last:border-0" data-testid={`admin-row-${item.id}`}>
                      <td className="py-2 pr-4">
                        {item.email}
                        {isSelf && (
                          <span className="ml-2 text-xs text-muted-foreground">(вы)</span>
                        )}
                      </td>
                      <td className="py-2 pr-4 text-muted-foreground">{formatDate(item.createdAt)}</td>
                      <td className="py-2 pr-4 text-muted-foreground">
                        {item.mustChangePassword ? 'Требуется' : '—'}
                      </td>
                      <td className="py-2 pr-4">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingAdmin(item)}
                            aria-label={`Редактировать ${item.email}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(item)}
                            disabled={isSelf || deletingId === item.id}
                            title={isSelf ? 'Нельзя удалить свой аккаунт' : undefined}
                            aria-label={`Удалить ${item.email}`}
                          >
                            {deletingId === item.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {showCreateModal && (
        <AdminFormModal onClose={() => setShowCreateModal(false)} onSaved={loadAdmins} />
      )}
      {editingAdmin && (
        <AdminFormModal admin={editingAdmin} onClose={() => setEditingAdmin(null)} onSaved={loadAdmins} />
      )}
    </div>
  );
}
